/**
 * React Query hook for Case Close Reasons (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { viewLeadClosureReasons } from 'services/lead/leadServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case close reasons data
 *
 * @param {Object} params - Query parameters
 * @param {boolean} params.isLead - Whether to fetch lead close reasons (default: false)
 * @param {boolean} params.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string} params.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} params.limit - Limit for fetching reasons (default: 10)
 * @param {string} params.token - Pagination token (default: '')
 * @param {string} params.direction - Pagination direction (default: 'forward')
 * @param {string} params.practice_area_id - Practice area filter (default: '')
 * @returns {Promise<Array>} - Array of close reasons formatted as {label, value}
 */
const fetchCaseCloseReasons = async ({
  isLead = false,
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  limit = 10,
  token = '',
  direction = 'forward',
  practice_area_id = '',
}) => {
  try {
    const response = await viewLeadClosureReasons(isLead, excludeFromLoading, sort_order, limit, token, direction, practice_area_id);

    // Transform data to match the format expected by components
    const closeReasonData =
      response?.data?.['closure-reason']?.map((val) => ({
        label: val.closure_reason,
        value: val.closure_reason,
      })) || [];

    return closeReasonData;
  } catch (error) {
    console.error('Error fetching case close reasons:', error);
    throw error;
  }
};

/**
 * Hook to fetch case close reasons with React Query
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLead - Whether to fetch lead close reasons (default: false)
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {number} options.limit - Limit for fetching reasons (default: 10)
 * @param {string} options.token - Pagination token (default: '')
 * @param {string} options.direction - Pagination direction (default: 'forward')
 * @param {string} options.practice_area_id - Practice area filter (default: '')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - caseCloseReasonList, isCloseReasonLoading, error, refetch, isFetching
 */
export const useCaseCloseReasonsQuery = ({
  isLead = false,
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  limit = 10,
  token = '',
  direction = 'forward',
  practice_area_id = '',
  enabled = true,
} = {}) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseCloseReasons.list(isLead, sort_order, limit, token, direction, practice_area_id),
    queryFn: () =>
      fetchCaseCloseReasons({
        isLead,
        excludeFromLoading,
        sort_order,
        limit,
        token,
        direction,
        practice_area_id,
      }),
    enabled,
    staleTime: 15 * 60 * 1000, // Consider data stale after 15 minutes (close reasons change less frequently)
    gcTime: 20 * 60 * 1000, // Keep in cache for 20 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  return {
    caseCloseReasonList: data || [],
    isCloseReasonLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
